import { useMemo, useCallback } from 'react';
import { useSchedule } from '../context/ScheduleContext';
import { WaitlistEntryRow } from './WaitlistEntryRow';

// ─── Icons ─────────────────────────────────────────────────────────────────

function UsersIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

// ─── WaitlistPanel ─────────────────────────────────────────────────────────

export function WaitlistPanel() {
  const { waitlistHandle } = useSchedule();
  const {
    entries,
    summary,
    pendingBackfills,
    machineState,
    requestWaitlistState,
    highlightedEntryId,
    setHighlightedEntryId,
  } = waitlistHandle;

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => a.priority - b.priority),
    [entries],
  );

  const activeOffers = pendingBackfills.filter((pb) => pb.status === 'offered');

  const handleRefresh = useCallback(() => {
    requestWaitlistState();
  }, [requestWaitlistState]);

  if (machineState === 'IDLE') {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <span style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', lineHeight: 1.6 }}>
          Waitlist unavailable.
          <br />
          <button
            onClick={handleRefresh}
            style={{ fontSize: 11, color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', marginTop: 4 }}
          >
            Retry
          </button>
        </span>
      </div>
    );
  }

  if (machineState === 'AWAITING_STATE') {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading waitlist…</span>
      </div>
    );
  }

  return (
    <>
      {/* ── Header ── */}
      <div style={{
        padding: '8px 10px 6px',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
        background: '#f8fafc',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: summary ? 5 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <UsersIcon />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Waitlist</span>
          </div>
          <button
            onClick={handleRefresh}
            title="Refresh waitlist"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '2px 4px', color: '#94a3b8', display: 'flex', alignItems: 'center', borderRadius: 3,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#475569'; (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
          >
            <RefreshIcon />
          </button>
        </div>

        {/* Summary stats */}
        {summary && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#475569' }}>
              <strong style={{ color: '#1e293b' }}>{summary.totalWaiting}</strong> waiting
            </span>
            {summary.urgentCount > 0 && (
              <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 600 }}>
                {summary.urgentCount} urgent
              </span>
            )}
            {summary.estimatedWaitDays > 0 && (
              <span style={{ fontSize: 10, color: '#64748b' }}>
                avg {summary.estimatedWaitDays}d wait
              </span>
            )}
            {summary.oldestWaitDays > 0 && (
              <span style={{ fontSize: 10, color: '#94a3b8' }}>
                oldest {summary.oldestWaitDays}d
              </span>
            )}
          </div>
        )}

        {/* In-flight offers */}
        {activeOffers.length > 0 && (
          <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%', background: '#f59e0b',
              animation: 'fse-backfill-pulse 1.5s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>
              {activeOffers.length} offer{activeOffers.length > 1 ? 's' : ''} in-flight
            </span>
          </div>
        )}
      </div>

      {/* ── Entry list ── */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#fff' }}>
        {sortedEntries.length === 0 ? (
          <div style={{ padding: '20px 12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            No patients on the waitlist
          </div>
        ) : (
          sortedEntries.map((entry) => (
            <WaitlistEntryRow
              key={entry.id}
              entry={entry}
              pendingBackfill={activeOffers.find((pb) => pb.patientId === entry.patientId)}
              highlighted={highlightedEntryId === entry.id}
              onHighlight={setHighlightedEntryId}
            />
          ))
        )}
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '4px 10px',
        borderTop: '1px solid #e2e8f0',
        fontSize: 11,
        color: '#94a3b8',
        flexShrink: 0,
        background: '#f8fafc',
      }}>
        {entries.length} {entries.length === 1 ? 'patient' : 'patients'} queued
      </div>
    </>
  );
}
